import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  AVAILABILITY_ROUTE,
  EXTRACT_ROUTE,
  HOME_ROUTE,
  JOB_QUEUE_ROUTE,
  AUTOMATION_ROUTE,
  CATEGORY_PAGES,
  DATABASE_ROUTE,
  MEETINGS_ROUTE,
  MEETINGS_CALENDAR_ROUTE,
  CALLERS_ROUTE,
  USERS_REGISTER_ROUTE,
  ACCOUNT_ROUTE,
  SLACK_BID_RESULTS_ROUTE,
  EMAIL_CHECKER_ROUTE,
  EMAIL_NOTICES_ROUTE,
  TEAMS_ROUTE,
  TEMPLATE_ROUTE,
} from '../constants';
import { assignedCategoryPages, hasAssignedProfiles, hasFeatureAccess, useAuth } from '../context/AuthContext';
import Icon from './Icon';

const ROLE_CONSOLE = {
  superadmin: 'Superadmin console',
  admin: 'Manager console',
  bidder: 'Bidder console',
  caller: 'Caller console',
};

/** Navigation groups visible to `user`; also feeds the top bar breadcrumb. */
export function navGroups(user) {
  const role = user?.role;
  const isSuper = role === 'superadmin';
  const isAdmin = isSuper || role === 'admin';
  const categoryPages = isSuper ? CATEGORY_PAGES : assignedCategoryPages(user);

  const groups = [
    {
      id: 'main',
      items: [{ to: HOME_ROUTE.path, end: true, icon: 'home', label: 'Dashboard' }],
    },
    {
      id: 'bidding',
      label: 'Bidding',
      items: [
        isSuper && { to: JOB_QUEUE_ROUTE.path, icon: 'briefcase', label: 'Job links' },
        isAdmin && { to: EXTRACT_ROUTE.path, icon: 'link', label: 'Extract links' },
        isSuper && { to: AUTOMATION_ROUTE.path, icon: 'sparkles', label: 'Auto apply' },
        isAdmin && { to: SLACK_BID_RESULTS_ROUTE.path, icon: 'image', label: 'Submissions' },
      ],
    },
    {
      id: 'resumes',
      label: 'Resumes',
      categories: isSuper || categoryPages.length > 0 ? categoryPages : null,
      items: [
        (isSuper || (role === 'admin' && hasAssignedProfiles(user))) && {
          to: TEMPLATE_ROUTE.path,
          icon: 'layout',
          label: 'Templates',
        },
      ],
    },
    {
      id: 'interviews',
      label: 'Interviews',
      items: [
        hasFeatureAccess(user, 'interviews') && { to: MEETINGS_ROUTE.path, end: true, icon: 'list', label: 'Schedule' },
        hasFeatureAccess(user, 'calendar') && { to: MEETINGS_CALENDAR_ROUTE.path, icon: 'calendar', label: 'Calendar' },
        isAdmin && { to: CALLERS_ROUTE.path, icon: 'phone', label: 'Callers' },
        (isAdmin || role === 'caller') && { to: AVAILABILITY_ROUTE.path, icon: 'clock', label: 'Availability' },
      ],
    },
    {
      id: 'workspace',
      label: 'Workspace',
      items: [
        isSuper && { to: USERS_REGISTER_ROUTE.path, icon: 'users', label: 'Users & bidders' },
        isSuper && { to: TEAMS_ROUTE.path, icon: 'layers', label: 'Teams' },
        isSuper && { to: DATABASE_ROUTE.path, icon: 'database', label: 'Database' },
        isAdmin && { to: EMAIL_CHECKER_ROUTE.path, icon: 'mail', label: 'Email checker' },
        isSuper && { to: EMAIL_NOTICES_ROUTE.path, icon: 'bell', label: 'Email notices' },
      ],
    },
  ];

  return groups
    .map((g) => ({ ...g, items: g.items.filter(Boolean) }))
    .filter((g) => g.items.length > 0 || (g.categories && g.categories.length > 0));
}

function SideLink({ to, icon, label, end, collapsed }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      className={({ isActive }) => `side-link${isActive ? ' active' : ''}`}
    >
      <Icon name={icon} />
      <span className="side-link-label">{label}</span>
    </NavLink>
  );
}

function Sidebar({ collapsed, mobileOpen, onToggleCollapsed, onCloseMobile }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const role = user?.role;
  const groups = navGroups(user);
  const categoryPages = groups.find((g) => g.categories)?.categories || [];
  const showAccount = ['superadmin', 'admin', 'caller', 'bidder'].includes(role);

  const categoryActive = categoryPages.some((c) => location.pathname === c.path);
  const [categoriesOpen, setCategoriesOpen] = useState(categoryActive);

  // Follow the route: open on a category page, fold away elsewhere. Manual toggles last until the next navigation.
  useEffect(() => {
    setCategoriesOpen(categoryActive);
  }, [categoryActive, location.pathname]);

  const navRef = useRef(null);
  useEffect(() => {
    const active = navRef.current?.querySelector('.side-link.active');
    active?.scrollIntoView({ block: 'nearest' });
  }, [location.pathname]);

  const toggleCategories = () => {
    // Collapsed rail has no room for the sub-list: expand the sidebar instead.
    if (collapsed) {
      onToggleCollapsed();
      setCategoriesOpen(true);
      return;
    }
    setCategoriesOpen((v) => !v);
  };

  const displayName = user?.display_name || user?.username || '';
  const initials = (displayName || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const userCard = (
    <>
      <span className="side-user-avatar" aria-hidden="true">{initials}</span>
      <span className="side-user-meta">
        <span className="side-user-name">{displayName}</span>
        <span className="side-user-role">
          {role}
          {user?.team_name ? ` · ${user.team_name}` : ''}
        </span>
      </span>
    </>
  );

  return (
    <aside
      className={`app-sidebar${collapsed ? ' is-collapsed' : ''}${mobileOpen ? ' is-mobile-open' : ''}`}
      aria-label="Main navigation"
    >
      <div className="side-brand">
        <NavLink to={HOME_ROUTE.path} className="brand-link" title="Satit & Cruce">
          <span className="brand-mark" aria-hidden="true">SC</span>
          <span className="brand-text">
            <span className="brand-title">Satit &amp; Cruce</span>
          </span>
        </NavLink>
        <button
          type="button"
          className="side-icon-btn side-collapse-btn"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name="sidebar" />
        </button>
        <button
          type="button"
          className="side-icon-btn side-close-btn"
          onClick={onCloseMobile}
          aria-label="Close menu"
        >
          <Icon name="close" />
        </button>
      </div>
      {role && (
        <div className="side-console" title={ROLE_CONSOLE[role] || role}>
          <Icon name="shield" size={13} />
          <span className="side-console-label">{ROLE_CONSOLE[role] || role}</span>
        </div>
      )}

      <nav className="side-nav" ref={navRef}>
        {groups.map((group) => (
          <div className="side-group" key={group.id}>
            {group.label && <p className="side-group-label">{group.label}</p>}
            {group.categories && group.categories.length > 0 && (
              <>
                <button
                  type="button"
                  className={`side-link side-link-toggle${categoryActive ? ' is-parent-active' : ''}`}
                  aria-expanded={categoriesOpen && !collapsed}
                  onClick={toggleCategories}
                  title={collapsed ? 'Categories' : undefined}
                >
                  <Icon name="fileText" />
                  <span className="side-link-label">Categories</span>
                  <Icon name="chevronDown" size={15} className={`side-caret${categoriesOpen ? ' is-open' : ''}`} />
                </button>
                {categoriesOpen && !collapsed && (
                  <div className="side-sublist">
                    {group.categories.map((cat) => (
                      <NavLink
                        key={cat.id}
                        to={cat.path}
                        className={({ isActive }) => `side-sublink${isActive ? ' active' : ''}`}
                      >
                        {cat.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            )}
            {group.items.map((item) => (
              <SideLink key={item.to} {...item} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      {user && (
        <div className="side-user">
          {showAccount ? (
            <NavLink
              to={ACCOUNT_ROUTE.path}
              className={({ isActive }) => `side-user-card${isActive ? ' active' : ''}`}
              title="Your account"
            >
              {userCard}
            </NavLink>
          ) : (
            <div className="side-user-card is-static">{userCard}</div>
          )}
          <button
            type="button"
            className="side-icon-btn side-signout"
            onClick={logout}
            aria-label="Sign out"
            title="Sign out"
          >
            <Icon name="logout" />
          </button>
        </div>
      )}
    </aside>
  );
}

export default Sidebar;
